import type { Ref } from 'vue';
import { ref, watch } from 'vue';

type UseUploadProgressTrackerReturn = {
    displayProgress: Ref<number>;
    start: () => void;
    pause: () => void;
    resume: () => void;
    reset: () => void;
    complete: () => void;
};

/** Mirrors the upload's real progress, gated so it can be frozen while offline and cleared on error. */
class ProgressTracker {
    public readonly displayProgress = ref<number>(0);
    private isRunning = false;

    public constructor(private readonly realProgress: Ref<number>) {
        watch(realProgress, progress => this.syncToRealProgress(progress));
    }

    /** Seeded from the real value, so progress already reported is not thrown away. */
    public start(): void {
        this.isRunning = true;
        this.syncToRealProgress(this.realProgress.value);
    }

    public pause(): void {
        this.isRunning = false;
    }

    public resume(): void {
        this.isRunning = true;
    }

    public reset(): void {
        this.isRunning = false;
        this.displayProgress.value = 0;
    }

    public complete(): void {
        this.isRunning = false;
        this.displayProgress.value = 100;
    }

    private syncToRealProgress(newProgress: number): void {
        if (!this.isRunning) {
            return;
        }

        if (newProgress >= 100) {
            this.complete();

            return;
        }

        this.displayProgress.value = newProgress;
    }
}

export function useUploadProgressTracker(realProgress: Ref<number>): UseUploadProgressTrackerReturn {
    const tracker = new ProgressTracker(realProgress);

    return {
        displayProgress: tracker.displayProgress,
        start: () => tracker.start(),
        pause: () => tracker.pause(),
        resume: () => tracker.resume(),
        reset: () => tracker.reset(),
        complete: () => tracker.complete(),
    };
}
